<script lang="ts">
	import Button from '$components/Button.svelte';
	import DOMPurify from 'dompurify';
	import { marked } from 'marked';
	import { motdLastSeenStore } from '../lib/motdStore';

	interface Props {
		content: string;
		checksum: string;
		title?: string;
		ondismiss?: () => void;
	}

	let { content, checksum, title = 'Message from the Admins', ondismiss }: Props = $props();

	function close(): void {
		// Store the checksum so we don't show this version again
		motdLastSeenStore.set(checksum);

		// Reset session flags so a NEW MOTD (different checksum) can show
		// But the same checksum won't show again this session
		if (typeof window !== 'undefined') {
			window.__motdShownThisSession = false;
			window.__motdCheckRan = false; // Allow check to run again for new MOTD
		}

		ondismiss?.();
	}

	// Configure marked the same way as the editor (v17: mangle/headerIds dropped).
	marked.use({
		breaks: true,
		gfm: true
	});

	// Convert markdown to HTML and sanitize. Reactive — content prop arrives
	// async after the MOTD fetch resolves, so this must re-run when it updates.
	let htmlContent = $derived(marked(content || '') as string);
	let sanitizedContent = $derived(
		DOMPurify.sanitize(htmlContent, {
			ALLOWED_TAGS: [
				'p',
				'h1',
				'h2',
				'h3',
				'h4',
				'h5',
				'h6',
				'strong',
				'em',
				'u',
				'ul',
				'ol',
				'li',
				'a',
				'img',
				'br',
				'div',
				'span',
				'code',
				'pre',
				'blockquote',
				'del',
				's',
				'strike'
			],
			ALLOWED_ATTR: [
				'href',
				'src',
				'alt',
				'title',
				'class',
				'style',
				'target',
				'rel',
				'width',
				'height'
			],
			// Allow data: URIs in src so MOTD inline base64 images render
			ALLOWED_URI_REGEXP: /^(?:(?:https?|ftp|mailto|tel|data):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i
		})
	);
</script>

<div class="flex max-h-[80vh] w-full max-w-2xl flex-col card p-5 font-manrope shadow-xl">
	<header class="flex-shrink-0 pb-3 text-center text-3xl font-extrabold">{title}</header>
	<div class="min-h-0 flex-1 overflow-x-hidden overflow-y-auto">
		<div class="markdown-content mb-3 p-5 text-justify font-manrope text-sm leading-5 font-medium">
			<!-- eslint-disable-next-line svelte/no-at-html-tags -- sanitizedContent is the result of `DOMPurify.sanitize(...)` (see L60-82); content is admin-authored MOTD HTML rendered after sanitization. -->
			{@html sanitizedContent}
		</div>
	</div>
	<div class="flex flex-shrink-0 gap-1 pt-3">
		<Button
			style="soft"
			variant="primary"
			classes="grow text-sm preset-tonal-primary"
			onclick={close}
		>
			<div>Got it!</div>
		</Button>
	</div>
</div>

<style>
	/* Reduce spacing in markdown content */
	:global(.markdown-content p) {
		margin-top: 0.5em;
		margin-bottom: 0.5em;
	}

	:global(.markdown-content h1),
	:global(.markdown-content h2),
	:global(.markdown-content h3),
	:global(.markdown-content h4),
	:global(.markdown-content h5),
	:global(.markdown-content h6) {
		margin-top: 0.75em;
		margin-bottom: 0.5em;
	}

	:global(.markdown-content ul),
	:global(.markdown-content ol) {
		margin-top: 0.5em;
		margin-bottom: 0.5em;
		padding-left: 1.5em;
	}

	:global(.markdown-content ul) {
		list-style-type: disc;
	}

	:global(.markdown-content ol) {
		list-style-type: decimal;
	}

	:global(.markdown-content li) {
		display: list-item;
		margin-top: 0.25em;
		margin-bottom: 0.25em;
	}

	:global(.markdown-content li > p) {
		margin-top: 0;
		margin-bottom: 0;
	}

	:global(.markdown-content img) {
		display: block;
		max-width: 100%;
		height: auto;
		margin-left: auto;
		margin-right: auto;
		margin-top: 0.5em;
		margin-bottom: 0.5em;
	}

	:global(.markdown-content a) {
		color: var(--color-primary-500);
		text-decoration: underline;
	}

	:global(.markdown-content code) {
		background-color: var(--color-surface-200);
		padding: 0.2em 0.4em;
		border-radius: 3px;
		font-family: 'Monaco', 'Courier New', monospace;
		font-size: 0.9em;
	}

	:global(.dark .markdown-content code) {
		background-color: var(--color-surface-700);
	}

	:global(.markdown-content pre) {
		background-color: var(--color-surface-200);
		padding: 1em;
		border-radius: 5px;
		overflow-x: auto;
		margin-top: 0.5em;
		margin-bottom: 0.5em;
	}

	:global(.dark .markdown-content pre) {
		background-color: var(--color-surface-700);
	}

	:global(.markdown-content pre code) {
		background-color: transparent;
		padding: 0;
	}

	:global(.markdown-content blockquote) {
		border-left: 4px solid var(--color-primary-500);
		padding-left: 1em;
		margin-left: 0;
		margin-top: 0.5em;
		margin-bottom: 0.5em;
		color: var(--color-surface-600);
	}

	:global(.dark .markdown-content blockquote) {
		color: var(--color-surface-300);
	}

	:global(.markdown-content strong) {
		font-weight: bold;
	}

	:global(.markdown-content em) {
		font-style: italic;
	}

	:global(.markdown-content del),
	:global(.markdown-content s),
	:global(.markdown-content strike) {
		text-decoration: line-through;
	}
</style>
